import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem, TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import StarRating from './StarRating';

// Mirrors Google Business Profile's official review-removal policy violations.
// https://support.google.com/business/answer/7400114
export const REMOVAL_REASONS = [
  { value: 'spam', label: 'Spam or fake content' },
  { value: 'off_topic', label: 'Off-topic (not about the business)' },
  { value: 'conflict_of_interest', label: 'Conflict of interest (competitor / employee)' },
  { value: 'profanity', label: 'Profanity, harassment, or hate speech' },
  { value: 'personal_info', label: 'Contains personal or confidential information' },
  { value: 'illegal', label: 'Promotes illegal or regulated content' },
  { value: 'not_client', label: 'Reviewer was never a client' },
  { value: 'other', label: 'Other (explain in notes)' },
];

const useStyles = makeStyles(() => ({
  reviewPreview: {
    background: '#F8F9FB',
    border: '1px solid #E4E7EB',
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    color: '#6C7480',
    marginBottom: 6,
  },
  previewAuthor: {
    fontWeight: 600,
    color: '#1A2332',
  },
  previewBody: {
    fontSize: 13,
    color: '#2D3748',
    lineHeight: 1.5,
  },
  intro: {
    fontSize: 13,
    color: '#4A5568',
    marginBottom: 14,
    lineHeight: 1.5,
  },
  field: {
    marginBottom: 14,
  },
  submit: {
    backgroundColor: '#00B5E2',
    color: 'white',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#009AC2',
    },
  },
  cancel: {
    textTransform: 'none',
  },
}));

const RemovalRequestDialog = ({
  open, review, onClose, onSubmit,
}) => {
  const classes = useStyles();
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setNotes('');
    }
  }, [open]);

  if (!review) return null;

  const canSubmit = reason !== '' && (reason !== 'other' || notes.trim().length > 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Request review removal</DialogTitle>
      <DialogContent>
        <div className={classes.intro}>
          Your Hennessey reputation specialist will review this flag and file the removal
          request with Google on your behalf. Google typically responds within 3–5 business days.
        </div>

        <div className={classes.reviewPreview}>
          <div className={classes.previewHeader}>
            <span className={classes.previewAuthor}>{review.author}</span>
            <StarRating value={review.rating} size={14} />
          </div>
          <div className={classes.previewBody}>{review.body}</div>
        </div>

        <FormControl variant="outlined" fullWidth className={classes.field}>
          <InputLabel id="removal-reason-label">Reason for removal</InputLabel>
          <Select
            labelId="removal-reason-label"
            label="Reason for removal"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            {REMOVAL_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          className={classes.field}
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          label={reason === 'other' ? 'Notes (required)' : 'Additional notes (optional)'}
          placeholder="Add any context that will help us argue for removal — e.g., the reviewer was never a client, the content references a different business, etc."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button className={classes.cancel} onClick={onClose}>Cancel</Button>
        <Button
          className={classes.submit}
          variant="contained"
          disabled={!canSubmit}
          onClick={() => onSubmit({ reason, notes: notes.trim() })}
        >
          Submit request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RemovalRequestDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  review: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

RemovalRequestDialog.defaultProps = {
  review: null,
};

export default RemovalRequestDialog;
